import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RoleStep } from "@/components/onboarding/RoleStep";
import Portfolio from "@/components/profile/Portfolio";
import { PersonalInfo } from "@/components/profile/PersonalInfo";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { OnboardingCard } from "@/components/onboarding/OnboardingCard";
import { useTranslation } from "react-i18next";
import { LanguageStep } from "@/components/onboarding/LanguageStep";
import { CreateOpportunityStep } from "@/components/onboarding/CreateOpportunityStep";

export default function Onboarding() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const { t } = useTranslation();
  const { 
    currentStep, 
    selectedRole, 
    isLoading, 
    setSelectedRole, 
    setIsLoading,
    handleStepComplete,
    handleStepBack,
    handleLanguageSelect 
  } = useOnboardingState();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/signup");
      } else {
        setUserId(session.user.id);
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setProfile(profileData);
      }
    };
    
    checkSession();
  }, [navigate]);

  const renderStep = () => {
    if (!userId) return null;

    switch (currentStep) {
      case 'language':
        return (
          <LanguageStep onLanguageSelect={handleLanguageSelect} />
        );
      case 'role':
        return (
          <RoleStep
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            onSubmit={handleRoleSubmit}
            isLoading={isLoading}
          />
        );
      case 'personal':
        return (
          <PersonalInfo
            profile={profile}
            onUpdate={() => {
              if (handleStepComplete()) {
                navigate("/profile");
              }
            }}
          />
        );
      case 'portfolio':
        return (
          <Portfolio
            userId={userId}
            isEditing={true}
            onComplete={() => {
              navigate("/profile");
            }}
          />
        );
      case 'opportunity':
        return (
          <CreateOpportunityStep
            onComplete={() => {
              navigate("/opportunities");
            }}
            onSkip={() => {
              navigate("/opportunities");
            }}
          />
        );
    }
  };

  const handleRoleSubmit = async () => {
    if (!selectedRole) {
      toast.error(t("onboarding.selectRole"));
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("No user found");
      }

      const { data: existingRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .single();

      if (existingRole) {
        if (existingRole.role === selectedRole) {
          handleStepComplete();
          return;
        }
        const { error: updateError } = await supabase
          .from("user_roles")
          .update({ role: selectedRole })
          .eq("user_id", user.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("user_roles")
          .insert({
            user_id: user.id,
            role: selectedRole
          });

        if (insertError) throw insertError;
      }

      handleStepComplete();
    } catch (error) {
      console.error("Role selection error:", error);
      toast.error(t("onboarding.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 sm:py-12">
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {t('onboarding.welcome')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {t('onboarding.description')}
          </p>
        </div>
        <OnboardingCard 
          step={currentStep} 
          selectedRole={selectedRole}
          onBack={currentStep !== 'language' ? handleStepBack : undefined}
        >
          {renderStep()}
        </OnboardingCard>
      </div>
    </div>
  );
}